import type { StaffRole } from '@ucp/web/lib';
import { ATR_REQUESTER_ROLES } from '../../lib/atr/atrRequesterRoles';

/**
 * Every role that holds an `atr_*` capability (capabilities.ts). Mirrors the
 * server role→capability map; `L6_PLATFORM_ADMIN` is DELIBERATELY absent (ADR-018
 * decision 3 — never an ATR shortcut). Extracted from `AtrSurface` into this
 * dependency-light module so the `AppRoutes` route guard can import it without
 * pulling the (now lazy-loaded) `AtrSurface` into the initial bundle. `AtrSurface`
 * re-exports it for back-compat. The requester roles come from the shared
 * `ATR_REQUESTER_ROLES` (PRO-3040), so a new requester role reaches the guard too.
 */
export const ATR_ROLES: readonly StaffRole[] = [
  ...ATR_REQUESTER_ROLES,
  'OPS_DIRECTOR',
  'FINANCE_APPROVER',
  'HR_REVIEWER',
  'ATR_COORDINATOR',
  'ATR_PANEL',
  'RECRUITER',
  'BUSINESS_DEVELOPMENT_DIRECTOR',
  'ESTATES_DIRECTOR',
  'FINANCE_DIRECTOR',
  'IT_DIRECTOR',
  'PEOPLE_DIRECTOR',
  'QUALITY_DIRECTOR',
  // PRO-1900 / ADR-072 A4 — executive chairs on the Finance approval gate. `COO`
  // is here because it holds `atr_executive_decision`, even though its other
  // capability is enterprise insights. Without these four the route guard hides
  // the ATR surface from a chair-only caller, who then has nowhere to land.
  'COO',
  'FINANCE_DIRECTOR_DEPUTY',
  'COO_DEPUTY',
  'PEOPLE_DIRECTOR_DEPUTY',
  // PRO-3190 — CEO reads All requests. Admission while the estate flag is off
  // is refused in the route guard, not by omitting the role from this list.
  'CEO',
];

/**
 * PRO-3190. CEO, and no other ATR role. A CEO who also holds COO is not CEO-only.
 */
export function atrAdmissionIsCeoOnly(
  roles: readonly { readonly role: StaffRole }[],
): boolean {
  if (!roles.some((assignment) => assignment.role === 'CEO')) {
    return false;
  }
  return !roles.some(
    (assignment) => assignment.role !== 'CEO' && ATR_ROLES.includes(assignment.role),
  );
}
